import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTeamPreferences } from '../../hooks/useTeamPreferences';
import { MODULE_CATALOG, type ModuleCatalogEntry } from '../../services/teamPreferences.service';
import SupportRequestModal from './SupportRequestModal';
import { InviteTeamMemberModal } from '../admin/InviteTeamMemberModal';
import { useAuth } from '../../contexts/AuthContext';
import { getLocaleLabel } from '../../constants/locales';
import { teamInviteService } from '../../services/backend/appwrite/teamInviteService';
import type { TeamMemberData, TeamInvitationData } from '../../types/n8nResponses';

/**
 * Team Preferences sections — Team / Modules, rendered inside the Settings
 * modal:
 *
 *   section="team"    — ABOUT THE TEAM: the Appwrite team roster (name, email,
 *                       language, role, TEAMADMIN label) + the invite button.
 *                       (The old team-defaults form now lives in Account →
 *                       Company — one place for shared company fields.)
 *   section="modules" — Modules & Products catalog (all members; requests only)
 *
 * Currency & timezone are NOT user-selectable — they derive automatically from
 * the selected Country + State (geo_countries.currency / geo_states.timezone),
 * the same sources used at registration.
 */

interface TeamPreferencesTabProps {
  /** Which Settings section to render. */
  section?: 'team' | 'modules';
  /** Close the parent Settings modal (e.g. before navigating to a module view). */
  onClose?: () => void;
}

export const TeamPreferencesTab: React.FC<TeamPreferencesTabProps> = ({ section = 'team', onClose }) => {
  const { data: teamPrefs } = useTeamPreferences();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentUser, targetEmail: impersonatedEmail } = useAuth();

  // ABOUT THE TEAM — the live Appwrite roster. team-invite-manager enriches
  // each confirmed member with their user labels (TEAMADMIN badge) and
  // language preference. Always-enabled so the cache is populated at boot.
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: roster, isLoading: rosterLoading } = useQuery({
    queryKey: ['team', 'roster', currentUser?.uid || ''],
    queryFn: async () => {
      const response = await teamInviteService.getTeamRoster({
        admin_user_id: currentUser?.uid || undefined,
        admin_email: impersonatedEmail || currentUser?.email || undefined,
      });
      return {
        teamMembers: (response.data?.team_members || []) as TeamMemberData[],
        teamInvitations: (response.data?.team_invitations || []) as TeamInvitationData[],
      };
    },
    staleTime: 60 * 1000,
    retry: 1,
  });
  const teamMembers = roster?.teamMembers || [];
  const teamInvitations = roster?.teamInvitations || [];

  // Module contracting goes through support — users cannot activate modules
  // themselves. Contract/Activate opens SupportRequestModal in the right mode:
  //  - outreach      → the dedicated Outreach infrastructure form
  //                    (unlock_outreach: weekly target + channels)
  //  - contract/free → contract_module support request
  const [requestingEntry, setRequestingEntry] = useState<ModuleCatalogEntry | null>(null);

  // ---- ABOUT THE TEAM: Appwrite team roster + invite (team section). ----
  const rosterSection = section === 'team' && (
    <div className="rounded-[24px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4 md:p-5">
      <div className="mb-3.5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-black tracking-tight text-white">About the team</h3>
          <p className="mt-0.5 text-[13px] leading-5 text-gray-400">
            Everyone with access to this workspace, straight from the Appwrite team.
          </p>
        </div>
        {/* Invite — opens the same modal used across the admin surfaces. */}
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-[#5260FF]/35 bg-[#5260FF] px-4 text-xs font-black uppercase tracking-[0.1em] text-white shadow-[0_12px_26px_rgba(82,96,255,0.25)] transition-colors hover:bg-[#4553f1]"
        >
          <Icon icon="bx:user-plus" className="h-4 w-4" />
          Invite member
        </button>
      </div>

      {rosterLoading ? (
        <div className="flex h-24 items-center justify-center text-sm text-white/40">
          <Icon icon="bx:loader-circle" className="mr-2 h-5 w-5 animate-spin" />Loading team members…
        </div>
      ) : teamMembers.length === 0 ? (
        <div className="flex h-24 items-center justify-center rounded-2xl border border-white/8 bg-black/20 text-sm text-white/40">
          No team members found.
        </div>
      ) : (
        <div className="divide-y divide-white/[0.06]">
          {teamMembers.map((member) => {
            const isTeamAdmin = member.labels?.includes('teamadmin') || member.labels?.includes('superadmin');
            const roleLabel = member.roles?.includes('owner')
              ? 'Owner'
              : member.roles?.[0]
                ? member.roles[0].replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                : 'Member';
            return (
              <div
                key={member.user_id || member.email}
                className="flex flex-wrap items-center gap-3 py-2.5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-xs font-black text-white/70">
                  {(member.name || member.email).split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?'}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-bold text-white">{member.name || member.email}</span>
                    {isTeamAdmin && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] text-cyan-100">
                        <Icon icon="bx:shield-quarter" className="h-3 w-3" />
                        Team admin
                      </span>
                    )}
                    {member.user_id === currentUser?.uid && (
                      <span className="rounded-full border border-white/10 bg-white/[0.05] px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] text-white/45">
                        You
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-gray-500">{member.email}</p>
                </div>
                {/* Language + role position — quiet text, no pills */}
                <span className="shrink-0 text-[12px] font-medium text-white/55">
                  {getLocaleLabel(member.language) || '—'}
                </span>
                <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-white/35">
                  {roleLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );


  return (
    <div className="space-y-4">
      {rosterSection}
      {/* Modules & Products — its own Settings section; visible to all members
          (requests only — activation is always support-managed). */}
      {section === 'modules' && (
      <div className="px-1 py-1 md:px-2">
        <div className="mb-3.5">
          <h3 className="text-base font-black tracking-tight text-white">Modules &amp; Products</h3>
          <p className="mt-0.5 text-[13px] leading-5 text-gray-400">
            Choose how your team works: manual ISR control, AI-managed outreach infrastructure, or additional AI products.
          </p>
        </div>

        <div className="divide-y divide-white/[0.06]">
          {(() => {
            // Read-only activation state from the Appwrite team prefs (mod_*
            // flags). Users can only REQUEST activation/contracting.
            const renderRow = (m: ModuleCatalogEntry, isSubFeature = false) => {
              const active = teamPrefs?.modules?.[m.field] ?? false;
              const opensNexus = m.field === 'nexus';
              // 'activation' = free included feature (amber Activate).
              // 'outreach_setup' and 'contract' = paid products (indigo Contract).
              const isFreeFeature = m.requestMode === 'activation';
              return (
                <div
                  key={m.field}
                  className={`grid grid-cols-[36px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 py-3 sm:grid-cols-[36px_minmax(0,1fr)_auto] ${isSubFeature ? 'ml-5 border-l border-white/[0.07] pl-4' : ''}`}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${active ? 'border-cyan-300/24 bg-cyan-400/10 text-cyan-100' : 'border-white/10 bg-white/[0.03] text-gray-500'}`}>
                    <Icon icon={m.icon} className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-cyan-100/45">{m.categoryLabel}</span>
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] ${active ? 'text-emerald-300/90' : 'text-white/30'}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-400' : 'bg-white/25'}`} />
                        {active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[13px] font-bold text-white">{m.label}</div>
                    <p className="mt-0.5 max-w-[58ch] text-[11px] leading-[1.45] text-gray-500">{m.description}</p>
                  </div>
                  <div className="col-start-2 flex items-center justify-between gap-3 sm:col-start-3 sm:row-start-1 sm:flex-col sm:items-end sm:justify-center">
                    {m.priceNote && <span className="whitespace-nowrap text-[10px] font-semibold text-white/38">{m.priceNote}</span>}
                    {opensNexus ? (
                      <button
                        type="button"
                        onClick={() => {
                          onClose?.();
                          navigate({ to: '/campaigns', search: { tab: 'nexus' } });
                        }}
                        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-cyan-300/25 bg-cyan-300/10 px-3 text-[11px] font-bold text-cyan-100 transition-colors hover:bg-cyan-300/16"
                      >
                        <Icon icon="bx:link-external" className="h-3.5 w-3.5" />
                        Open Nexus
                      </button>
                    ) : active ? (
                      <span className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-emerald-300/20 bg-emerald-400/8 px-3 text-[11px] font-bold text-emerald-200">
                        <Icon icon="bx:check" className="h-3.5 w-3.5" />
                        Activated
                      </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRequestingEntry(m)}
                      className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-[11px] font-bold transition-all ${
                        isFreeFeature
                          // Amber = "action needed / request" — visually distinct
                          // from the green Activated state and the indigo Contract.
                          ? 'border-amber-300/30 bg-amber-400/10 text-amber-200 hover:bg-amber-400/16'
                          : 'border-[#5260FF]/30 bg-[#5260FF]/12 text-indigo-200 hover:bg-[#5260FF]/20'
                      }`}
                    >
                      <Icon icon={isFreeFeature ? 'bx:rocket' : 'bx:plus'} className="h-3.5 w-3.5" />
                      {isFreeFeature ? 'Activate' : 'Contract'}
                    </button>
                  )}
                  </div>
                </div>
              );
            };

            const parents = MODULE_CATALOG.filter((m) => !m.subFeatureOf);
            return parents.map((parent) => (
              <div key={parent.field}>
                {renderRow(parent)}
                {MODULE_CATALOG
                  .filter((m) => m.subFeatureOf === parent.field)
                  .map((sub) => renderRow(sub, true))}
              </div>
            ));
          })()}
        </div>

        <div className="mt-3 flex items-start gap-2 border-t border-white/[0.06] pt-3 text-amber-100/70">
          <Icon icon="bx:info-circle" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p className="text-[11px] leading-5 text-amber-100/60">
            Activation and contracts are managed by our team.
          </p>
        </div>
      </div>
      )}

      {/* Module requests — routed by requestMode:
          outreach_setup → the dedicated Outreach infrastructure form
          activation/contract → contract_module support request */}
      <SupportRequestModal
        isOpen={requestingEntry !== null}
        onClose={() => setRequestingEntry(null)}
        userEmail={currentUser?.email}
        userName={currentUser?.displayName}
        impersonatedEmail={impersonatedEmail}
        clientInfo={teamPrefs ? {
          id: teamPrefs.lvl1_id || '',
          name: teamPrefs.team_name || '',
          email: teamPrefs.billing_email || currentUser?.email || '',
          hasOutreachModule: teamPrefs.modules.outreach,
        } : null}
        mode={requestingEntry?.requestMode === 'outreach_setup' ? 'unlock_outreach' : 'contract_module'}
        requestedModules={requestingEntry ? [requestingEntry.label] : []}
      />

      {/* Invite flow — the same modal used across the admin surfaces. */}
      {section === 'team' && (
        <InviteTeamMemberModal
          isOpen={inviteOpen}
          onClose={() => {
            setInviteOpen(false);
            // Pull the roster fresh so new members/invites appear immediately.
            void queryClient.invalidateQueries({ queryKey: ['team', 'roster', currentUser?.uid || ''] });
          }}
          teamMembers={teamMembers}
          teamInvitations={teamInvitations}
        />
      )}
    </div>
  );
};

export default TeamPreferencesTab;
